import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { SYMPTOM_CATALOG, DEFAULT_ENABLED_SYMPTOMS } from '../../utils/wellness';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'white',
      border: '1px solid #E8DFD2',
      borderRadius: '8px',
      padding: '10px 14px',
      fontSize: '0.82rem',
      boxShadow: '0 4px 12px rgba(92,74,58,0.1)',
    }}>
      <p style={{ fontWeight: 600, color: '#5C4A3A', marginBottom: '4px' }}>{label}</p>
      {payload.map((p) => (
        p.value !== null && (
          <p key={p.dataKey} style={{ color: p.color }}>
            {p.name}: {p.value}/10
          </p>
        )
      ))}
    </div>
  );
}

export default function WellnessTrendsChart({ data, events = [], enabledSymptoms = null }) {
  const symptoms = enabledSymptoms || DEFAULT_ENABLED_SYMPTOMS;
  const activeSymptoms = SYMPTOM_CATALOG.filter((s) => symptoms.includes(s.key));

  // Subsample if too many points
  const sampled = data.length > 60
    ? data.filter((_, i) => i % Math.ceil(data.length / 30) === 0 || i === data.length - 1)
    : data;

  // Map events to dates for reference lines
  const eventDates = events.map((e) => {
    const d = new Date(e.date + 'T12:00:00');
    return {
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      description: e.description,
    };
  });

  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={sampled} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8DFD2" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#B8A48E' }}
            tickLine={false}
            axisLine={{ stroke: '#E8DFD2' }}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[0, 10]}
            ticks={[0, 2, 4, 6, 8, 10]}
            tick={{ fontSize: 11, fill: '#B8A48E' }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '0.72rem' }}
            iconSize={8}
          />

          {/* Protocol change markers */}
          {eventDates.map((evt, i) => (
            <ReferenceLine
              key={i}
              x={evt.date}
              stroke="#B8A48E"
              strokeDasharray="4 4"
              strokeWidth={1}
              label={{
                value: '⬥',
                position: 'top',
                fill: '#7A6651',
                fontSize: 12,
              }}
            />
          ))}

          {activeSymptoms.map((symptom) => (
            <Line
              key={symptom.key}
              type="monotone"
              dataKey={symptom.key}
              name={symptom.label}
              stroke={symptom.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: symptom.color }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Event legend */}
      {events.length > 0 && (
        <div style={{ marginTop: '10px', paddingLeft: '4px' }}>
          <p style={{ fontSize: '0.72rem', color: '#B8A48E', marginBottom: '4px' }}>
            ⬥ Protocol changes:
          </p>
          {events.slice(0, 5).map((evt, i) => (
            <p key={i} style={{ fontSize: '0.72rem', color: '#7A6651', lineHeight: 1.5 }}>
              {new Date(evt.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {evt.description}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
